/**
 * `/settings/devices` — paired Macs, iPhones and the browser extension.
 *
 * Nothing is readable yet: the `devices` table exists in the schema design but
 * has no contract procedure, and the API's JWT and api-key paths still answer
 * 501 (device auth lands separately). So this states the pairing model the
 * design fixes — which is genuinely useful documentation for the person about to
 * install the Mac app — and shows no device list at all rather than an empty one
 * that implies "you have none".
 */

import { Card, CardContent, CardHeader, CardTitle } from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { NotWired } from '../../../components/not-wired.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';

export const Route = createFileRoute('/_authed/settings/devices')({
  component: DevicesSettings,
  errorComponent: RouteErrorPanel,
});

const CREDENTIALS: readonly { readonly client: string; readonly credential: string }[] = [
  { client: 'This web app', credential: 'better-auth session cookie (SameSite=Lax, HttpOnly)' },
  { client: 'iPhone', credential: 'JWT from the better-auth jwt plugin, in expo-secure-store' },
  {
    client: 'macOS recorder',
    credential: 'api-key + Secure Enclave P-256 DPoP proof, in Keychain',
  },
  { client: 'Chrome extension', credential: 'OAuth 2.1 + PKCE + non-extractable WebCrypto DPoP' },
];

function DevicesSettings() {
  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>How each client authenticates</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[10rem_1fr] gap-y-1.5 text-sm">
            {CREDENTIALS.map((row) => (
              <div key={row.client} className="contents">
                <dt className="text-muted-foreground">{row.client}</dt>
                <dd>{row.credential}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <NotWired
        title="Device pairing, revocation and last-seen"
        blockedOn="Device auth is landing separately; the API's JWT and api-key paths return 501 today and there is no devices.* procedure"
      >
        Once paired devices are readable this screen lists each one with its last check-in and a
        revoke action — the &quot;silent for over 24 hours&quot; Mac warning that feeds the
        connection strip is computed from exactly that timestamp.
      </NotWired>
    </div>
  );
}
